import React, { useEffect, useMemo, useState } from "react";
import type { SelectedEntity } from "../model/types";
import { formatRelativeCount } from "../model/formatting";
import {
  fetchSourceEntityColumns,
  type SourceEntityColumnMapping
} from "../../../services/dataVaultModelling.service";

const FALLBACK_ENVIRONMENT_ID = 114;
const FALLBACK_CUSTOMER_ID = 101;

const parseNumericId = (value: string | number | null | undefined): number | null => {
  if (value === null || value === undefined || value === "") {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const getWorkspaceContextIds = () => {
  if (typeof window === "undefined") {
    return {
      environmentId: FALLBACK_ENVIRONMENT_ID,
      customerId: FALLBACK_CUSTOMER_ID
    };
  }
  const environmentRaw = window.localStorage.getItem("environment_id");
  const customerRaw = window.localStorage.getItem("customer_id");
  return {
    environmentId: parseNumericId(environmentRaw) ?? FALLBACK_ENVIRONMENT_ID,
    customerId: parseNumericId(customerRaw) ?? FALLBACK_CUSTOMER_ID
  };
};

export function MappingSummary({
  entity,
  onOpenMappings,
  disabled = false
}: {
  entity: Exclude<SelectedEntity, null>;
  onOpenMappings: () => void;
  disabled?: boolean;
}): JSX.Element {
  const entityId = entity.entity.id;
  const localColumnCount = entity.entity.columns?.length ?? 0;
  const [columnSummaryByEntity, setColumnSummaryByEntity] = useState<
    Record<string, { count: number; checked: boolean }>
  >({});

  const cachedSummary = columnSummaryByEntity[entityId];
  const resolvedColumnCount =
    localColumnCount > 0 ? localColumnCount : cachedSummary?.count ?? 0;
  const hasResolvedColumnState =
    localColumnCount > 0 || Boolean(cachedSummary?.checked);
  const mappingLabel = formatRelativeCount(resolvedColumnCount, "column", "columns");
  const linkTableMappingLabel =
    entity.type === "link"
      ? formatRelativeCount(entity.entity.mappings.length, "table mapping", "table mappings")
      : null;

  useEffect(() => {
    if (localColumnCount > 0) {
      setColumnSummaryByEntity((previous) => {
        const next = previous[entityId];
        if (next?.checked && next.count === localColumnCount) {
          return previous;
        }
        return {
          ...previous,
          [entityId]: { count: localColumnCount, checked: true }
        };
      });
      return undefined;
    }

    const existingSummary = cachedSummary;
    if (existingSummary?.checked) {
      return undefined;
    }

    const sourceEntityId = parseNumericId(entity.entity.record.source_entity_id);
    if (!sourceEntityId) {
      setColumnSummaryByEntity((previous) => {
        const next = previous[entityId];
        if (next?.checked && next.count === 0) {
          return previous;
        }
        return {
          ...previous,
          [entityId]: { count: 0, checked: true }
        };
      });
      return undefined;
    }

    const { environmentId, customerId } = getWorkspaceContextIds();
    const targetEntityRdvId = parseNumericId(entity.entity.record.target_entity_rdv_id);
    let isMounted = true;

    fetchSourceEntityColumns({
      source_entity_id: sourceEntityId,
      environment_id: environmentId,
      customer_id: customerId,
      for_modelling: true,
      target_entity_rdv_id: targetEntityRdvId ?? undefined
    })
      .then((response) => {
        if (!isMounted) {
          return;
        }
        const rows: SourceEntityColumnMapping[] = Array.isArray(response)
          ? response
          : Array.isArray(response?.items)
          ? response.items
          : [];
        setColumnSummaryByEntity((previous) => ({
          ...previous,
          [entityId]: { count: rows.length, checked: true }
        }));
      })
      .catch(() => {
        if (!isMounted) {
          return;
        }
        setColumnSummaryByEntity((previous) => ({
          ...previous,
          [entityId]: { count: 0, checked: true }
        }));
      });

    return () => {
      isMounted = false;
    };
  }, [
    cachedSummary,
    entity.entity.record.source_entity_id,
    entity.entity.record.target_entity_rdv_id,
    entityId,
    localColumnCount
  ]);

  const infoText = useMemo(() => {
    if (hasResolvedColumnState && resolvedColumnCount === 0) {
      return "No columns captured for this entity.";
    }
    if (hasResolvedColumnState && resolvedColumnCount > 0) {
      return `${mappingLabel} captured for this entity.`;
    }
    return "Checking column mappings…";
  }, [hasResolvedColumnState, resolvedColumnCount, mappingLabel]);

  return (
    <section className="tw-rounded-2xl tw-border tw-border-slate-100 tw-bg-white tw-p-5">
      <div className="tw-flex tw-flex-col tw-gap-3 sm:tw-flex-row sm:tw-items-center sm:tw-justify-between">
        <div className="tw-flex-1">
          <p className="tw-text-[0.68rem] tw-font-semibold tw-uppercase tw-tracking-[0.28em] tw-text-slate-500">
            Column mapping
          </p>
          <p className="tw-mt-1 tw-text-sm tw-font-medium tw-text-slate-700">
            Map source columns to this entity
          </p>
          <p className="tw-mt-1 tw-text-xs tw-text-slate-500">
            {infoText}
            {resolvedColumnCount > 0 && linkTableMappingLabel ? ` · ${linkTableMappingLabel}` : ""}
          </p>
        </div>
        <button
          type="button"
          onClick={disabled ? undefined : onOpenMappings}
          disabled={disabled}
          className="tw-w-full tw-rounded-full tw-border tw-border-[#03234a] tw-bg-[#03234a] tw-px-4 tw-py-2 tw-text-sm tw-font-semibold tw-text-white tw-transition hover:tw-bg-[#021b39] hover:tw-border-[#021b39] focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-offset-2 focus:tw-ring-[#03234a] disabled:tw-cursor-not-allowed disabled:tw-opacity-60 sm:tw-w-auto"
        >
          Open mapping
        </button>
      </div>
    </section>
  );
}
